import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { FeatureErrorBoundary } from '@/core/error-boundaries/public';
import { sentryReporter } from '@/core/observability/public';
import { Button } from '@/shared/components/ui/button';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { useResetPassword } from '../hooks/useResetPassword';
import { createResetPasswordSchema, type ResetPasswordFormValues } from '../schemas';

export function ResetPasswordPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  // Solo llega aquí navegación interna desde VerifyRecoveryCodePage tras
  // verificar el código — no hay enlace de correo ni token en la URL.
  const isVerified = (location.state as { verified?: boolean } | null)?.verified ?? false;
  const { updatePassword, isPending, isSuccess, error } = useResetPassword();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const { control, handleSubmit } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(createResetPasswordSchema(t)),
    defaultValues: { password: '', confirmPassword: '' },
  });

  const onSubmit = ({ password }: ResetPasswordFormValues) => {
    updatePassword(password);
  };

  useEffect(() => {
    if (!isSuccess) return;
    const timeout = setTimeout(() => navigate('/login', { replace: true }), 2500);
    return () => clearTimeout(timeout);
  }, [isSuccess, navigate]);

  // Sin verificación previa del código: no vino del paso 2 — no hay nada
  // que establecer aquí.
  if (!isVerified && !isSuccess) {
    return (
      <Card className="w-full border-0 shadow-none bg-transparent">
        <CardHeader className="text-center">
          <CardTitle className="text-base font-medium">{t('resetPassword.invalidLink')}</CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          <p className="text-muted-foreground text-sm text-center mb-4">
            {t('resetPassword.invalidLinkDesc')}
          </p>
          <Button variant="outline" className="w-full" onClick={() => navigate('/forgot-password')}>
            {t('resetPassword.backToForgot')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (isSuccess) {
    return (
      <Card className="w-full border-0 shadow-none bg-transparent">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-2">
            <CheckCircle2 className="h-10 w-10 text-primary" />
          </div>
          <CardTitle className="text-base font-medium">{t('resetPassword.success')}</CardTitle>
          <p className="text-muted-foreground text-sm">{t('resetPassword.successDesc')}</p>
        </CardHeader>
      </Card>
    );
  }

  const passwordToggle = (
    <button
      type="button"
      data-testid="auth-reset-password-toggle"
      onClick={() => setShowPassword((v) => !v)}
      aria-label={showPassword ? t('aria.hide_password') : t('aria.show_password')}
      className="text-muted-foreground hover:text-foreground transition-colors"
    >
      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
    </button>
  );

  const confirmToggle = (
    <button
      type="button"
      data-testid="auth-reset-confirm-password-toggle"
      onClick={() => setShowConfirm((v) => !v)}
      aria-label={showConfirm ? t('aria.hide_password') : t('aria.show_password')}
      className="text-muted-foreground hover:text-foreground transition-colors"
    >
      {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
    </button>
  );

  return (
    <FeatureErrorBoundary reporter={sentryReporter}>
      <Card className="w-full border-0 shadow-none bg-transparent">
        <CardHeader className="text-center">
          <CardTitle className="text-base font-medium">{t('resetPassword.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 px-0">
          {error && (
            <Alert variant="destructive" data-testid="auth-reset-error-alert">
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          )}
          <form
            id="reset-password-form"
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-6"
          >
            <div className="flex flex-col gap-4">
              <FormFieldInput
                control={control}
                name="password"
                label={t('resetPassword.newPassword')}
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                floatingLabel
                endElement={passwordToggle}
                data-testid="auth-reset-password-input"
              />
              <FormFieldInput
                control={control}
                name="confirmPassword"
                label={t('resetPassword.confirmPassword')}
                type={showConfirm ? 'text' : 'password'}
                autoComplete="new-password"
                floatingLabel
                endElement={confirmToggle}
                data-testid="auth-reset-confirm-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={isPending}
              aria-busy={isPending}
              data-testid="auth-reset-submit"
            >
              {isPending ? t('resetPassword.submitting') : t('resetPassword.submit')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </FeatureErrorBoundary>
  );
}
